import type { PontoEquilibrioResponse } from '@triade/contracts';
import { ehDecimal, formatarDinheiro, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';

export type GaugePe = NonNullable<PontoEquilibrioResponse['gauge']>;

/** Geometria do velocimetro (viewBox 320 x 175). So desenho: nenhum numero de negocio sai daqui. */
const LARGURA = 320;
const ALTURA = 175;
const CX = 160;
const CY = 150;
const R = 100;
const ESPESSURA = 14;
const RAIO_ROTULO = R + ESPESSURA / 2 + 14;

/** Fracao da API (0..1) como numero, limitada a 0..1. Invalida: null. Conversao so para desenhar. */
export function fracaoParaNumero(fracao: string | null | undefined): number | null {
  if (!ehDecimal(fracao)) return null;
  return Math.min(Math.max(Number(fracao), 0), 1);
}

/** Angulo do ponteiro em graus (0 = para cima; -90 = extremo esquerdo; +90 = extremo direito): fracao x 180 - 90. */
export function anguloPonteiro(fracao: string | null | undefined): number | null {
  const f = fracaoParaNumero(fracao);
  return f === null ? null : f * 180 - 90;
}

/** Valor do atributo `transform` do ponteiro (rotacao em torno do centro do arco). Invalido: null. */
export function transformacaoPonteiro(fracao: string | null | undefined): string | null {
  const ang = anguloPonteiro(fracao);
  return ang === null ? null : `rotate(${ang} ${CX} ${CY})`;
}

/** Ponto sobre o arco (raio dado) na fracao 0..1: 0 = extremo esquerdo, 1 = extremo direito. */
export function pontoNoArco(f: number, raio: number): { x: number; y: number } {
  const theta = Math.PI * f;
  return { x: CX - raio * Math.cos(theta), y: CY - raio * Math.sin(theta) };
}

/** Caminho SVG da faixa do arco entre duas fracoes (sentido horario sobre o topo). Faixa vazia: null. */
export function caminhoFaixa(de: number, ate: number): string | null {
  if (ate <= de) return null;
  const a = pontoNoArco(de, R);
  const b = pontoNoArco(ate, R);
  return `M ${a.x} ${a.y} A ${R} ${R} 0 0 1 ${b.x} ${b.y}`;
}

/** Rotulo da marca do PE: "PE R$ 45.714,29" (texto pronto da API, formatado so por lib/format). */
export function rotuloPe(pontoEquilibrio: string): string {
  return `PE ${formatarMoeda(pontoEquilibrio)}`;
}

/** Resumo para leitores de tela (aria-label do grafico). */
export function resumoMostrador(g: GaugePe): string {
  const base = `Faturamento ${formatarMoeda(g.faturamento)}, ponto de equilíbrio ${formatarMoeda(g.pontoEquilibrio)}`;
  return g.margemSeguranca === null ? base : `${base}, margem de segurança ${formatarFracaoPercentual(g.margemSeguranca)}`;
}

export interface MostradorPeProps {
  gauge: GaugePe;
  /** Largura maxima do mostrador (o pai decide o espaco disponivel). */
  className?: string;
}

/**
 * Mostrador semicircular (velocimetro) do ponto de equilibrio: faixa vermelha ate o PE (prejuizo) e verde dali ate o
 * maximo (lucro); marca do PE; ponteiro no faturamento. As posicoes (fracoes 0..1) e os textos vem prontos da API.
 */
export function MostradorPe({ gauge: g, className }: MostradorPeProps) {
  const fatura = fracaoParaNumero(g.posFaturamento) ?? 0;
  const pe = fracaoParaNumero(g.posPontoEquilibrio) ?? 0;
  const faixaPrejuizo = caminhoFaixa(0, pe);
  const faixaLucro = caminhoFaixa(pe, 1);
  const marca = pontoNoArco(pe, R);
  const rotuloMarca = pontoNoArco(pe, RAIO_ROTULO);
  const xRotulo = Math.min(Math.max(rotuloMarca.x, 50), LARGURA - 50);
  const ponteiro = transformacaoPonteiro(g.posFaturamento);

  return (
    <div className={cn('mx-auto flex w-full flex-col items-center', className)} data-testid="mostrador-pe">
      <svg viewBox={`0 0 ${LARGURA} ${ALTURA}`} className="h-auto w-full" role="img" aria-label={resumoMostrador(g)}>
        <path d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`} fill="none" stroke="currentColor" strokeWidth={ESPESSURA} className="text-line-soft" />
        {faixaPrejuizo && (
          <path d={faixaPrejuizo} fill="none" stroke="currentColor" strokeWidth={ESPESSURA} className="text-destructive" data-testid="faixa-prejuizo" />
        )}
        {faixaLucro && <path d={faixaLucro} fill="none" stroke="currentColor" strokeWidth={ESPESSURA} className="text-success" data-testid="faixa-lucro" />}
        <line x1={marca.x} y1={marca.y} x2={rotuloMarca.x} y2={rotuloMarca.y} stroke="currentColor" strokeWidth={2} className="text-ink" data-testid="marca-pe" />
        <text x={xRotulo} y={rotuloMarca.y - 4} textAnchor="middle" className="fill-current text-ink tabular" fontSize={11} data-testid="rotulo-pe">
          {rotuloPe(g.pontoEquilibrio)}
        </text>
        {ponteiro && (
          <g transform={ponteiro} data-testid="ponteiro">
            <line x1={CX} y1={CY - 6} x2={CX} y2={CY - (R - 8)} stroke="currentColor" strokeWidth={3} strokeLinecap="round" className="text-ink" />
          </g>
        )}
        <circle cx={CX} cy={CY} r={5} fill="currentColor" className="text-ink" />
        <text x={CX} y={CY - 30} textAnchor="middle" fontSize={11} className="fill-current text-ink-secondary tabular" data-testid="rotulo-faturamento">
          {`Faturamento ${formatarMoeda(g.faturamento)}`}
        </text>
      </svg>
      <dl className="mt-2 grid w-full grid-cols-2 gap-x-4 gap-y-2 text-small">
        <div>
          <dt className="text-caption text-ink-muted">Faturamento do período</dt>
          <dd className="tabular font-medium">{formatarMoeda(g.faturamento)}</dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">Ponto de equilíbrio (R$)</dt>
          <dd className="tabular font-medium">{formatarDinheiro(g.pontoEquilibrio)}</dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">Margem de segurança</dt>
          <dd className={cn('tabular font-medium', g.margemSeguranca !== null && g.margemSeguranca.startsWith('-') && 'text-danger-ink')}>
            {g.margemSeguranca === null ? '—' : formatarFracaoPercentual(g.margemSeguranca)}
          </dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">MC média</dt>
          <dd className="tabular font-medium">{formatarFracaoPercentual(g.mcMedia)}</dd>
        </div>
      </dl>
    </div>
  );
}
